import { useState, useEffect, useCallback } from "react";
import { Form, message } from "antd";

// ✅ Imports DIRECTS des helpers (pas de barrel)
import getInitialEditValues from "../functions/profile/getInitialEditValues";
import getExistingAvatarPreview from "../functions/profile/getExistingAvatarPreview";
import fileToDataURL from "../functions/profile/fileToDataURL";
import makeEditPayload from "../functions/profile/makeEditPayload";

/**
 * Gère l'état du formulaire, l’image et le submit pour EditUserModal.
 * Le parent (useUserInformation) fait les appels réseau via onSubmit(payload).
 */
export function useEditUser({ open, user, medical, onSubmit }) {
  const [form] = Form.useForm();
  const [imagePreview, setImagePreview] = useState(null);
  const [fileList, setFileList] = useState([]);

  // Pré-remplissage quand la modale s’ouvre
  useEffect(() => {
    if (!open) return;

    form.setFieldsValue(getInitialEditValues(user, medical));  // first/last/email/...
    setImagePreview(getExistingAvatarPreview(user, medical));  // url absolue ou null
    setFileList([]);                                          // reset upload
  }, [open, user, medical, form]);

  // Choix d’une image → aperçu base64
  const handleImageChange = useCallback(async ({ fileList: next }) => {
    setFileList(next || []);
    const file = next?.[0]?.originFileObj;
    setImagePreview(await fileToDataURL(file));
  }, []);

  const handleCancel = useCallback(
    (onClose) => {
      form.resetFields();
      setImagePreview(null);
      setFileList([]);
      onClose?.();
    },
    [form]
  );

  const handleFinish = useCallback(
    async (values, onClose) => {
      try {
        // birthdate → ISO ; avatar → File|null
        const payload = makeEditPayload(values, fileList);
        await onSubmit?.(payload);
        handleCancel(onClose); // close & reset
      } catch (err) {
        // eslint-disable-next-line no-console
        message.error(
          typeof err === "string"
            ? err
            : err?.message || "❌ Une erreur est survenue lors de la mise à jour."
        );
      }
    },
    [fileList, onSubmit, handleCancel]
  );

  return {
    form,
    imagePreview,
    fileList,
    setFileList,
    handleImageChange,
    handleFinish,
    handleCancel,
  };
}

export default useEditUser;
